import {css, StyleSheet} from 'aphrodite';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {RestrictionEnzymeDropdown} from 'molbio/components/RestrictionEnzymeDropdown';
import EnzymeConstants, {DigestableEnzyme, NO_ENZYME_SENTINEL} from 'molbio/enzymes/EnzymeConstants';
import * as EnzymeUtil from 'molbio/enzymes/EnzymeUtil';
import {
  isRequiredLinkFolderItemType,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import useTagSchemaQuery from 'schemas/useTagSchemaQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';

import {TagSchemaGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';
import {Step, Steps} from '../../BulkWorkflowComponents';
import * as BulkAssemblyActions from '../BulkAssemblyActions';
import * as BulkAssemblyConstants from '../BulkAssemblyConstants';
import * as BulkAssemblyUtil from '../BulkAssemblyUtil';
import DataSourceSelector from './DataSourceSelector';
import DetectHomologyButton from './DetectHomologyButton';
import EndpointTypeDropdown from './EndpointTypeDropdown';
import GibsonSlotEndpoint from './GibsonSlotEndpoint';
import {NavButtonStyles, NavigationPreviousButton} from './NavigationButtons';
import PositionalEndpointOptions from './PositionalEndpointOptions';

const Fragment = {
  LARGE: 'LARGE',
  SMALL: 'SMALL',
};

const Direction = {
  FORWARD: 'FORWARD',
  REVERSE: 'REVERSE',
};

export interface QueryingSlotEditorProps {
  assembly: BulkAssemblyConstants.LocalAssembly;
  slotIdx: number;
  onOpenPrimerSettings: () => void;
}

interface SlotEditorProps extends QueryingSlotEditorProps {
  tagSchema: TagSchemaGraphQL | null;
}

export class SlotEditor extends React.Component<SlotEditorProps, {}> {
  static propTypes = {
    assembly: BulkAssemblyConstants.assembly.isRequired,
    slotIdx: PropTypes.number.isRequired,
  };

  _getEndpoint(index: number): BulkAssemblyConstants.Endpoint {
    return this._getSlot().slotParams.endpoints[index];
  }

  _getSchemaField(): SchemaField | null {
    if (!this.props.assembly.tagSchemaId || !this._getSlot().slotParams.schemaFieldId) {
      return null;
    }
    const schemaFields: Array<SchemaField> = this.props.tagSchema!.fieldsJson;
    // eslint-disable-next-line no-restricted-syntax
    const matchingSchemaFields = _.filter(
      schemaFields,
      (schemaField: SchemaField) => schemaField.apiId === this._getSlot().slotParams.schemaFieldId
    );
    assertAndContinue(
      matchingSchemaFields.length === 1,
      `Could not find schema field with ID ${this._getSlot().slotParams.schemaFieldId}`
    );
    return matchingSchemaFields[0];
  }

  _getSlot(): BulkAssemblyConstants.Slot {
    return this.props.assembly.slots[this.props.slotIdx];
  }

  _handleRemoveClick = (): void => {
    BulkAssemblyActions.removeSlot(this.props.slotIdx);
  };

  _handleEnzymeEndpointChange(index: number, {enzymeId}: {enzymeId: number}): void {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.EnzymeEndpoint;
    this._updateEndpoint(index, {...endpoint, enzymeId});
  }

  _handleHomologyEndpointChange(index: number, {bases}: {bases: string}): void {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.HomologyEndpoint;
    this._updateEndpoint(index, {...endpoint, bases});
  }

  _handlePositionalEndpointChange(index: number, changes: {position: number} | {bases: string}): void {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.ConcatenationEndpoint;
    this._updateEndpoint(index, {...endpoint, position: null, bases: null, ...changes});
  }

  _handleGibsonEndpointChange(index: number, changes: {position: number} | {bases: string}): void {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.GibsonEndpoint;
    this._updateEndpoint(index, {...endpoint, position: null, bases: null, ...changes});
  }

  _handleFragmentReverseChange = (e: InputChangeEvent): void => {
    BulkAssemblyActions.updateSlot(this.props.slotIdx, {
      useReverse: e.target.value === Direction.REVERSE,
    });
  };

  _handleFragmentSizeChange = (e: InputChangeEvent): void => {
    BulkAssemblyActions.updateSlot(this.props.slotIdx, {
      useLargerFragment: e.target.value === Fragment.LARGE,
    });
  };

  _handleEndpointTypeChange(index: number, type: BulkAssemblyConstants.EndpointType): void {
    this._updateEndpoint(index, BulkAssemblyUtil.getDefaultEndpointArgs(type));
  }

  _handleSchemaFieldChange(schemaFieldId: string | null): void {
    BulkAssemblyActions.updateSlot(this.props.slotIdx, {
      schemaFieldId,
    });
  }

  _createSchemaFieldSelectDropdown(schemaFields: Array<SchemaField>): JSX.Element {
    const placeholderText = 'Select a schema field...';
    return (
      <SelectDropdown
        displayName={this._getSchemaField() ? this._getSchemaField()!.name : placeholderText}
        menu={
          <Menu scrollable={true} isMulti={false} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
            <Option
              key="none"
              value={null}
              onSelect={() => {
                this._handleSchemaFieldChange(null);
              }}
            >
              {placeholderText}
            </Option>
            {schemaFields.map((tagSchemaField) => (
              <Option
                key={tagSchemaField.apiId}
                value={tagSchemaField.apiId}
                onSelect={(schemaFieldId: string) => {
                  this._handleSchemaFieldChange(schemaFieldId);
                }}
              >
                {tagSchemaField.name}
              </Option>
            ))}
          </Menu>
        }
      />
    );
  }

  _renderEnzymeSuggestions(endpointIndex: number): JSX.Element | null {
    const suggestedEnzymes = BulkAssemblyUtil.getSuggestedEnzymes(
      this.props.assembly,
      this.props.slotIdx,
      endpointIndex
    );
    if (suggestedEnzymes.length === 0) {
      return null;
    }
    return (
      <div>
        Currently used:{' '}
        {suggestedEnzymes.map(({enzymeId, slotName}, i) => (
          <React.Fragment key={enzymeId}>
            {i > 0 && ', '}
            <Tooltip title={slotName}>
              <a
                onClick={() => {
                  this._handleEnzymeEndpointChange(endpointIndex, {enzymeId});
                }}
              >
                {EnzymeUtil.getEnzyme(enzymeId).name}
              </a>
            </Tooltip>
          </React.Fragment>
        ))}
      </div>
    );
  }

  _renderEnzymeEndpoint(index: number): JSX.Element {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.EnzymeEndpoint;
    return (
      <div
        className="bulkAssemblyEditor-slotEditor-endpoint-field"
        {...getTestProps({component: 'SlotEditor', element: 'enzymeSelector'})}
      >
        <FieldLabel label="Enzyme" />
        <RestrictionEnzymeDropdown
          allowedRestrictionEnzymeIds={EnzymeConstants.enzymes
            .filter((enzyme: DigestableEnzyme) => !EnzymeUtil.isDoubleCutterEnzyme(enzyme))
            .map((enzyme: DigestableEnzyme) => enzyme.id)}
          restrictionEnzymeId={endpoint.enzymeId ?? NO_ENZYME_SENTINEL}
          updateRestrictionEnzymeId={(enzymeId: number) => {
            this._handleEnzymeEndpointChange(index, {
              enzymeId,
            });
          }}
          shouldAllowNull={false}
          isDisabled={false}
        />
        {this._renderEnzymeSuggestions(index)}
      </div>
    );
  }

  _renderHomologyBaseSuggestions(endpointIndex: number): JSX.Element | null {
    const suggestedBaseSearches = BulkAssemblyUtil.getSuggestedHomologyBases(
      this.props.assembly,
      this.props.slotIdx,
      endpointIndex
    );
    if (suggestedBaseSearches.length === 0) {
      return null;
    }
    return (
      <div>
        Currently used:{' '}
        {suggestedBaseSearches.map(({bases, slotName}, i) => (
          <React.Fragment key={bases}>
            {i > 0 && ', '}
            <Tooltip title={slotName}>
              <a
                onClick={() => {
                  this._handleHomologyEndpointChange(endpointIndex, {bases});
                }}
                {...getTestProps({component: 'SlotEditor', element: 'homologyRegionSuggestion'})}
              >
                {bases}
              </a>
            </Tooltip>
          </React.Fragment>
        ))}
      </div>
    );
  }

  _renderHomologyEndpoint(index: number): JSX.Element {
    const {assembly, slotIdx} = this.props;
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.HomologyEndpoint;
    return (
      <>
        <div className="bulkAssemblyEditor-slotEditor-endpoint-field">
          <label>Bases</label>
          <div className="u-position-relative">
            <input
              type="text"
              onChange={(e) => {
                this._handleHomologyEndpointChange(index, {bases: e.target.value});
              }}
              value={endpoint.bases}
            />
            {endpoint.bases.length === 0 && (
              <DetectHomologyButton
                {...{assembly, slotIdx}}
                changeBases={(bases) => {
                  this._handleHomologyEndpointChange(index, {bases});
                }}
              />
            )}
          </div>
          {this._renderHomologyBaseSuggestions(index)}
        </div>
      </>
    );
  }

  _renderConcatenationEndpoint(index: number): JSX.Element {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.ConcatenationEndpoint;
    return (
      <PositionalEndpointOptions
        endpoint={endpoint}
        handlePositionalEndpointChange={(changes) => {
          this._handlePositionalEndpointChange(index, changes);
        }}
      />
    );
  }

  _renderGibsonEndpoint(index: number): JSX.Element {
    const endpoint = this._getEndpoint(index) as BulkAssemblyConstants.GibsonEndpoint;
    return (
      <GibsonSlotEndpoint
        endpoint={endpoint}
        onEndpointChange={(changes) => {
          this._handleGibsonEndpointChange(index, changes);
        }}
        onOpenPrimerSettings={this.props.onOpenPrimerSettings}
      />
    );
  }

  _renderEndpointWithType(index: number): JSX.Element | null {
    const endpoint = this._getEndpoint(index);
    switch (endpoint.type) {
      case BulkAssemblyConstants.EndpointType.ENZYME:
        return this._renderEnzymeEndpoint(index);
      case BulkAssemblyConstants.EndpointType.HOMOLOGY:
        return this._renderHomologyEndpoint(index);
      case BulkAssemblyConstants.EndpointType.CONCATENATION:
        return this._renderConcatenationEndpoint(index);
      case BulkAssemblyConstants.EndpointType.GIBSON:
        return this._renderGibsonEndpoint(index);
      default:
        throw new Error('Unexpected endpoint type');
    }
  }

  _renderEndpoint(index: number): JSX.Element {
    return (
      <div className="u-vertical-stretch">
        <div
          className="bulkAssemblyEditor-slotEditor-endpoint-field"
          {...getTestProps({component: 'SlotEditor', element: 'endpointTypeField'})}
        >
          <label>Type</label>
          <EndpointTypeDropdown
            getEndpoint={(index) => this._getEndpoint(index)}
            handleEndpointTypeChange={(index, type) => {
              this._handleEndpointTypeChange(index, type);
            }}
            index={index}
          />
        </div>
        {this._renderEndpointWithType(index)}
      </div>
    );
  }

  _updateEndpoint(index: number, newEndpoint: BulkAssemblyConstants.Endpoint): void {
    const slot = this._getSlot();
    if (index === 0) {
      BulkAssemblyActions.updateSlot(this.props.slotIdx, {
        endpoints: [newEndpoint, slot.slotParams.endpoints[1]],
      });
    } else {
      BulkAssemblyActions.updateSlot(this.props.slotIdx, {
        endpoints: [slot.slotParams.endpoints[0], newEndpoint],
      });
    }
  }

  render(): JSX.Element {
    const slot = this._getSlot();
    const schemaFields = this.props.assembly.tagSchemaId
      ? (this.props.tagSchema?.fieldsJson ?? []).filter(
          (tagSchemaField: SchemaField) =>
            !tagSchemaField.isComputed &&
            tagSchemaField.requiredLink != null &&
            (isRequiredLinkTagSchema(tagSchemaField.requiredLink)
              ? tagSchemaField.requiredLink.tagSchema != null &&
                tagSchemaField.requiredLink.tagSchema.folderItemType === 'sequence'
              : isRequiredLinkFolderItemType(tagSchemaField.requiredLink)
                ? tagSchemaField.requiredLink.folderItemType === 'sequence'
                : false)
        )
      : [];
    return (
      <div>
        <Steps>
          <Step text="Select files to work with">
            <DataSourceSelector assembly={this.props.assembly} slotIdx={this.props.slotIdx} />
          </Step>
          {schemaFields && schemaFields.length > 0 && (
            <Step text="Select a schema field that this sequence fills (optional)">
              {this._createSchemaFieldSelectDropdown(schemaFields)}
              <div className={css(styles.helpText)}>
                Part link fields are not included here. To use part link fields, use the Autofill Parts
                feature after bulk assembling sequences.
              </div>
            </Step>
          )}
          <Step text="Select fragment direction">
            <div
              className="u-flex-display"
              {...getTestProps({component: 'SlotEditor', element: 'fragmentDirection'})}
            >
              <div className="bulkAssemblyEditor-slotEditor-endpoint-field">
                <div
                  className="bulkAssemblyEditor-slotEditor-fragment radio"
                  {...getTestProps({
                    component: 'SlotEditor',
                    element: 'fragmentDirectionOption',
                    key: 'forward',
                  })}
                >
                  <label>
                    <input
                      type="radio"
                      checked={!slot.slotParams.useReverse}
                      value={Direction.FORWARD}
                      onChange={this._handleFragmentReverseChange}
                      {...getTestProps({
                        component: 'SlotEditor',
                        element: 'fragmentDirectionOptionRadio',
                      })}
                    />
                    Forward
                  </label>
                </div>
              </div>
              <div className="bulkAssemblyEditor-slotEditor-endpoint-field">
                <div
                  className="bulkAssemblyEditor-slotEditor-fragment radio"
                  {...getTestProps({
                    component: 'SlotEditor',
                    element: 'fragmentDirectionOption',
                    key: 'reverse',
                  })}
                >
                  <label>
                    <input
                      type="radio"
                      checked={slot.slotParams.useReverse}
                      value={Direction.REVERSE}
                      onChange={this._handleFragmentReverseChange}
                      {...getTestProps({
                        component: 'SlotEditor',
                        element: 'fragmentDirectionOptionRadio',
                      })}
                    />
                    Reverse
                  </label>
                </div>
              </div>
            </div>
          </Step>
          <Step text="Select fragment end #1">{this._renderEndpoint(0)}</Step>
          <Step text="Select fragment end #2">{this._renderEndpoint(1)}</Step>
          <Step text="Select fragment size">
            <div
              className="u-flex-display"
              {...getTestProps({component: 'SlotEditor', element: 'fragmentSize'})}
            >
              <div className="bulkAssemblyEditor-slotEditor-endpoint-field">
                <div
                  className="bulkAssemblyEditor-slotEditor-fragment radio"
                  {...getTestProps({component: 'SlotEditor', element: 'fragmentSizeOption', key: 'large'})}
                >
                  <label>
                    <input
                      type="radio"
                      checked={slot.slotParams.useLargerFragment}
                      value={Fragment.LARGE}
                      onChange={this._handleFragmentSizeChange}
                      {...getTestProps({component: 'SlotEditor', element: 'fragmentSizeOptionRadio'})}
                    />
                    Larger
                  </label>
                </div>
              </div>
              <div
                className="bulkAssemblyEditor-slotEditor-endpoint-field"
                {...getTestProps({component: 'SlotEditor', element: 'fragmentSizeOption', key: 'small'})}
              >
                <div className="bulkAssemblyEditor-slotEditor-fragment radio">
                  <label>
                    <input
                      type="radio"
                      checked={!slot.slotParams.useLargerFragment}
                      value={Fragment.SMALL}
                      onChange={this._handleFragmentSizeChange}
                      {...getTestProps({
                        component: 'SlotEditor',
                        element: 'fragmentSizeOptionRadio',
                      })}
                    />
                    Smaller
                  </label>
                </div>
              </div>
            </div>
          </Step>
        </Steps>

        <div className={css(NavButtonStyles.btnWrapper)}>
          <NavigationPreviousButton assembly={this.props.assembly} />
          <div className={css(NavButtonStyles.btn)}>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              onClick={BulkAssemblyActions.transitionToNextSlot}
              text="Next"
              dataTestAttrs={{component: 'SlotEditor', element: 'nextButton'}}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default function QueryingSlotEditor(props: QueryingSlotEditorProps): JSX.Element {
  const tagSchemaId = props.assembly.tagSchemaId;
  const {tagSchema, isLoading} = useTagSchemaQuery(tagSchemaId);

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  } else {
    return <SlotEditor {...props} tagSchema={tagSchema} />;
  }
}

const styles = StyleSheet.create({
  helpText: {
    color: GRAY,
  },
});
